import { FC, ChangeEvent, CSSProperties, Fragment } from 'react';
import {
  Box,
  makeStyles,
  Tab,
  TabProps,
  Tabs,
  Theme,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import { WbButton } from '../WbButton';
import { WbTabProps } from './WbTab';
import { WbDivider } from '../WbDivider';

const useStyles = makeStyles(
  (theme: Theme) => ({
    container: {
      display: 'flex',
      borderRadius: 24,
      background: theme.palette.bkg.primary,
      flexWrap: 'wrap',
    },
    tab: {
      padding: '6px 12px',
      position: 'relative',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
    },
    divider: {
      width: 2,
      background: theme.palette.blueGrey[200],
      display: 'block',
      alignSelf: 'center',
    },
    defaultTab: {
      color: theme.palette.text.primary,
      opacity: 1,
      fontWeight: 500,
      margin: '0 8px',
      minWidth: 0,
      padding: 0,
      letterSpacing: '0.009rem',
      fontSize: '1.125rem',
      '&:first-child': {
        marginLeft: 0,
      },
    },
    tabRoot: {
      '&:hover': {
        backgroundColor: 'transparent',
        color: theme.palette.primary.main,
      },
    },
    selected: {
      color: theme.palette.primary.main,
    },
  }),
  { name: 'WbTabs' },
);

interface WbTabsProps extends WbTabProps {
  selected?: boolean;
  tabProps?: TabProps;
}

interface Props {
  tabs: Array<WbTabsProps>;
  wrapperClassName?: string;
  containerClassName?: string;
  selectedClassName?: string;
  variant?: 'switch' | 'default';
  handleChange?: (_: ChangeEvent<{}>, newValue: number) => void;
  selectedTab?: any;
  tabStyle?: CSSProperties;
  scrollable?: boolean;
}

export const WbTabs: FC<Props> = ({
  tabs,
  containerClassName,
  selectedClassName,
  wrapperClassName,
  variant = 'switch',
  handleChange,
  selectedTab,
  tabStyle,
  scrollable = false,
}) => {
  const classes = useStyles();
  return variant === 'switch' ? (
    <Box className={clsx(wrapperClassName)}>
      <Box
        className={clsx(classes.container, containerClassName)}
        role="tablist"
        aria-label="Switch tabs"
      >
        {tabs.map(({ name, action, selected }, index) => {
          const hasDivider = index >= 0 && index < tabs.length - 1;
          return (
            <Fragment key={name}>
              <WbButton
                className={clsx(
                  classes.tab,

                  selected && classes.selected,
                  selected && selectedClassName,
                )}
                onClick={action}
                variant="text"
                color="inherit"
                role="tab"
                aria-selected={selected}
                aria-controls={`tabpanel-${index}`}
                label={
                  <Typography style={{ fontWeight: 500 }}>{name}</Typography>
                }
                style={{
                  padding: '6px 12px',
                  minWidth: 0,
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
              />
              {hasDivider && (
                <WbDivider className={classes.divider} height="18px" />
              )}
            </Fragment>
          );
        })}
      </Box>
    </Box>
  ) : (
    <Tabs
      selectionFollowsFocus
      indicatorColor="primary"
      aria-label="tabs"
      onChange={handleChange}
      value={selectedTab}
      style={tabStyle}
      variant={scrollable ? 'scrollable' : 'standard'}
      scrollButtons="auto"
    >
      {tabs.map((tab, index) => (
        <Tab
          {...tab.tabProps}
          data-testid={`header-tab-${index}`}
          label={tab.name}
          key={tab.name}
          value={index}
          className={classes.defaultTab}
          classes={{ selected: classes.selected, root: classes.tabRoot }}
        />
      ))}
    </Tabs>
  );
};
